import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Layers,
  Grid3X3,
  Percent,
  Square,
  Triangle,
  Anchor,
  Zap,
  Wind,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Lightbulb,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Link } from 'react-router-dom';

const ICON_MAP = {
  layers: Layers,
  grid: Grid3X3,
  percent: Percent,
  square: Square,
  triangle: Triangle,
  anchor: Anchor,
  zap: Zap,
  wind: Wind,
  rewind: RotateCcw,
};

const CATEGORY_COLORS = {
  print_quality: { text: 'text-blue-400', badge: 'bg-blue-700' },
  structure: { text: 'text-green-400', badge: 'bg-green-700' },
  supports: { text: 'text-purple-400', badge: 'bg-purple-700' },
  adhesion: { text: 'text-yellow-400', badge: 'bg-yellow-700' },
  performance: { text: 'text-orange-400', badge: 'bg-orange-600' },
};

const CATEGORY_LABELS = {
  print_quality: 'Print Quality',
  structure: 'Structure',
  supports: 'Supports',
  adhesion: 'Adhesion',
  performance: 'Performance',
};

const FILTERS = [
  { id: 'all', label: 'All Settings' },
  { id: 'print_quality', label: 'Quality' },
  { id: 'structure', label: 'Structure' },
  { id: 'supports', label: 'Supports' },
  { id: 'adhesion', label: 'Adhesion' },
  { id: 'performance', label: 'Performance' },
];

const SlicerGuide = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/printing3d/slicer-guide`)
      .then(res => setEntries(res.data || []))
      .catch(err => console.error('Failed to load slicer guide:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpanded = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const filtered = filter === 'all' ? entries : entries.filter(e => e.category === filter);

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.PRINTING3D_SLICER)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Layers className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading slicer guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="slicer-guide" style={bgStyle(IMAGES.PRINTING3D_SLICER)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/3d-printing" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Layers className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">SLICER SETTINGS</div>
            <h1
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Slicer Guide
            </h1>
            <p className="text-sm text-zinc-300 mt-1">
              Layer height, infill, supports, speeds, cooling, and retraction — explained
            </p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {FILTERS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === id ? 'bg-orange-500 text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Guide entries */}
      <div className="space-y-4 max-w-4xl">
        {filtered.map((entry) => {
          const colors = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.performance;
          const Icon = ICON_MAP[entry.icon] || Layers;
          const isOpen = expanded[entry.id];

          return (
            <Card key={entry.id} className="bg-zinc-900 border-zinc-800">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => toggleExpanded(entry.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-zinc-800 ${colors.text}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${colors.badge} text-white text-xs`}>
                          {CATEGORY_LABELS[entry.category] || entry.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-white text-lg">{entry.title}</CardTitle>
                      <CardDescription className="text-zinc-400 text-sm mt-0.5">{entry.summary}</CardDescription>
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  )}
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-6 pt-0">
                  {/* Presets table */}
                  {entry.presets && entry.presets.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-3">Presets</h4>
                      <div className="overflow-x-auto rounded-lg border border-zinc-800">
                        <table className="w-full text-sm">
                          <thead className="bg-zinc-800">
                            <tr>
                              <th className="px-4 py-2 text-left text-zinc-400 font-medium">Name</th>
                              <th className="px-4 py-2 text-left text-zinc-400 font-medium">Value</th>
                              {entry.presets[0]?.speed && (
                                <th className="px-4 py-2 text-left text-zinc-400 font-medium">Speed</th>
                              )}
                              {entry.presets[0]?.strength && (
                                <th className="px-4 py-2 text-left text-zinc-400 font-medium">Strength</th>
                              )}
                              {entry.presets[0]?.materials && (
                                <th className="px-4 py-2 text-left text-zinc-400 font-medium">Materials</th>
                              )}
                              <th className="px-4 py-2 text-left text-zinc-400 font-medium">Use Case</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.presets.map((preset, idx) => (
                              <tr
                                key={idx}
                                className={idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800/40'}
                              >
                                <td className="px-4 py-2.5 text-white font-medium">{preset.name}</td>
                                <td className="px-4 py-2.5 text-orange-400 font-mono">{preset.value}</td>
                                {preset.speed !== undefined && (
                                  <td className="px-4 py-2.5 text-zinc-300">{preset.speed}</td>
                                )}
                                {preset.strength !== undefined && (
                                  <td className="px-4 py-2.5 text-zinc-300">{preset.strength}</td>
                                )}
                                {preset.materials !== undefined && (
                                  <td className="px-4 py-2.5 text-zinc-300 text-xs">{preset.materials}</td>
                                )}
                                <td className="px-4 py-2.5 text-zinc-400 text-xs">{preset.use_case}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Rules */}
                  {entry.rules && entry.rules.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        Rules of Thumb
                      </h4>
                      <ul className="space-y-2">
                        {entry.rules.map((rule, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-zinc-300 text-sm">
                            <span className="text-green-400 flex-shrink-0 mt-0.5">•</span>
                            {rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendation */}
                  {entry.recommendation && (
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
                      <Lightbulb className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <p className="text-orange-200 text-sm">{entry.recommendation}</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <Layers className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No entries for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlicerGuide;
